












































import Vue, { PropType } from 'vue'
import { mapGetters } from 'vuex'
import Media from '@/types/Media.interface'
import Message, { MessageSendType } from '@/types/Message.interface'
import MessagePreviewBubble from '@/components/MessagePreviewBubble.vue'
import MessagePreviewInvoices from '@/components/MessagePreviewInvoices.vue'
import MessagePreviewTemplates from '@/components/MessagePreviewTemplates.vue'
import Person from '@/types/Person.interface'
import mixin from '@/mixins/message-mixin'
import moment from 'moment-timezone'
import ScheduleBuilder from '@/components/ScheduleBuilder.vue'
import { randomKey } from '@/utility/helpers'
export default Vue.extend({
    mixins: [mixin],
    components: {
        MessagePreviewBubble,
        MessagePreviewInvoices,
        MessagePreviewTemplates,
        ScheduleBuilder
    },
    props: {
        //  A callback function executed after a message has been sent
        callback: {
            type: Function,
            required: true
        },
        //  Array of invoices provided when mass sending invoices. Each containing message has it's invoice attached
        invoiceMessages: {
            type: Array as PropType<Message[]>,
            default: null,
            required: false
        },
        //  The media attached to the image
        media: {
            type: Object as PropType<Media>,
            default: null,
            required: false
        },
        //  The message we're going to post
        message: {
            type: Object as PropType<Message>,
            default: null,
            required: true
        },
        //  The person we're sending the message to
        person: {
            type: Object as PropType<Person>,
            default: null,
            required: false
        },
        //  Bool to disable "Add Schedule" in ScheduleBuilder.vue
        scheduleSingle: {
            type: Boolean,
            default: false,
            required: false
        },
        //  Determines if this is a 'scheduling' or a 'send now' preview
        type: {
            type: String as PropType<MessageSendType>,
            default: null,
            required: true
        }
    },
    data() {
        return {
            //  Loading indicator
            loading: null,
            //  Determines if a template is created/updated when the message is sent
            saveTemplate: false,
            //  The template that will be saved during the message send.
            selectedTemplate: null,
            //  Blocks the message send if the recipient has missing tag data
            tagBlock: true,
            //  Indicates if the schedules attached to the message are valid
            schedulesValid: false
        }
    },
    mounted() {
        //  Focus on the preview wrapper, so we can use 'shift+enter' to send the message
        setTimeout(() => {
            this.$refs.previewMessage.focus()
        }, 100)

        //  If this message is type 'schedule' we need to make sure a timezone is set
        if (this.type === 'schedule') {
            //  Add a new schedule to the schedule builder
            if (!this.message.schedules) this.addSchedule()
        }

        //  If we're not on a mobile device, focus on the send button (so user can quickly send with enter key)
        if (!this.isMobile()) this.focus()
    },
    computed: {
        ...mapGetters([
            'authIsAdmin',
            'isMobile',
            'smsAccount',
            'stripeCustomerHasPayment',
            'stripeCustomer',
            'tenant',
            'user'
        ]),
        //  Boolean indicating if schedules are attached to the message
        isScheduled(): boolean {
            return this.message.schedules && this.message.schedules.length > 0
        },
        //  Boolean indicating if an invoice is attached to the message
        isInvoice(): boolean {
            return this.invoiceMessages && this.invoiceMessages.length > 0 ? true : false
        },
        //  Find missing tag data in the message recipient
        parsedText(): string {
            //  If the channel does NOT have a selected person, return empty array
            if (!this.person) return this.message.text

            //  Get the raw text from the message
            let text = this.message.text

            //  Strip HTML tags from our text
            text = text.replace(/(<([^>]+)>)/gi, '')

            //  Get the message tags for the person
            const tags = this.tagValues(this.person)

            //  Replace message tags with data
            for (const [key, value] of Object.entries(tags)) {
                //  Check if the tag exists in the message text
                const hasTag = text.includes(key)

                //  If the tag does NOT exist, continue to next iteration
                if (!hasTag) continue

                //  If the 'styled' param is set, wrap the value in html style, else use plain value
                const html = value
                    ? value
                    : `<span class="red--text font-weight-bold">${key}</span>`

                //  Replace the tag(s) with the html value
                text = text.replaceAll(key, html)
            }

            return text
        },
        phoneNumber(): string {
            return 'Phone: ' + this.$options.filters.personDefaultPhone(this.person)
        },
        //  Indicates if the 'scheduling' feature is enabled for this tenant & user
        schedulingEnabled(): boolean {
            //  Check if subscription plan allows it
            return this.$store.getters.getEnabled('scheduling')
        },
        //  Determines if the send button is disabled
        sendDisabled(): boolean {
            //  If we're trying to save a template AND it is not set, disable the send button
            if (this.saveTemplate && !this.selectedTemplate) return true

            //  Otherwise, enable the send button
            return false
        }
    },
    methods: {
        addSchedule(): void {
            //  If schedules do NOT exist, create empty array
            if (!this.message.schedules) this.message.schedules = []

            //  Add a new schedule to the message
            this.message.schedules.push({
                next_send: null,
                interval: 'SEND ONCE'
            })

            //  Force update the UI
            this.$nextTick(() => this.$forceUpdate())
        },
        disabledPopup(): void {
            //  Open a dialog informing user they cannot access x feature (in this case inbox settings)
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Button Disabled',
                text: "You don't have permission to access this feature. Please contact your account's admin to perform this task or have them update your account permission",
                icon: 'mdi-alert',
                color: 'primary',
                closeable: true
            })
        },
        //  Focus on the send button
        focus(): void {
            /**
             *  Focus on the button, so the user can hit enter to send the message.
             *  Using a timeout is janky, but it seems to be the only thing that works
             *  when trying to focus on an element in a modal.
             */
            setTimeout(() => {
                if (this.$refs.sendButton) this.$refs.sendButton.$el.focus()
            }, 200)
        },
        goToWorkspaceSettings(): void {
            //  If the user is an operator we don't let them procede as they cannot access inbox settings, instead we inform them to ask their admin
            if (!this.$store.getters.userHasPermission(2)) {
                return this.disabledPopup()
            }

            //  Route to workspace
            this.$router.push('/settings?open=workspace')

            //  Make sure to close this dialog
            this.$store.dispatch('dialogClose', 'APP')
        },
        //  Validate and send all message requests, routes the message to the appropriate handling
        async messageSend(event: PointerEvent | KeyboardEvent): Promise<void> {
            //  If this

            //  Set saved to true by default
            let saved = true

            //  Else, if we're saving the message as a template, wait for that to finish
            if (this.saveTemplate === true) saved = await this.templateSave()

            //  If we failed to save, stop because something happened
            if (!saved) return

            //  If we are sending invoice messages, escape and go to special handling
            if (this.isInvoice) return this.sendInvoiceMessages()

            //  Else, If this is a scheduled message, escape and go to special handling
            if (this.isScheduled) return this.sendScheduledMessage()

            //  Else, this is a standard message, so send it!
            this.sendStandardMessage()
        },
        notifyMessageSend(type: string): void {
            //  Select the appropriate notification message based on send type
            const title = {
                standard: 'Message(s) Sent',
                invoices: 'Invoice(s) Sent',
                scheduled: `Message scheduled!`,
                scheduledInvoices: 'Processing Scheduled Invoices'
            }[type]

            const description =
                {
                    scheduled: `Check the 'Scheduled' filter for a list of all scheduled messages.`,
                    scheduledInvoices:
                        'We are currently processing your scheduled invoices. Please allow a few seconds for the invoices to update on this page.'
                }[type] || ''

            //  Pop notification
            this.$store.commit('notificationPopupOpen', {
                title: title,
                text: description,
                icon: 'bi-chat-square-dots-fill',
                id: randomKey()
            })
        },
        //  The event that occurs when messages have been successfully posted to the API
        onMessagesSent(type: string, messages: Message[] = null): void {
            //  After message has been sent (standard or schedule) toggle force loading if it's on
            if (this.loading) this.loading = false

            //  Close the message preview
            this.$store.dispatch('dialogClose', 'APP')

            //  Post messages to the sms panel, and reset the message form
            this.callback(messages)

            //  Notify of the messages being sent
            this.notifyMessageSend(type)
        },
        // Reset schedules attached to the message
        resetSchedules(): void {
            //  Set the message's schedules to null
            this.message.schedules = null

            //  Force update the UI
            this.$nextTick(() => this.$forceUpdate())
        },
        //  Post invoice messages to the API
        async sendInvoiceMessages(): Promise<void> {
            //  Check if the invoices messages are scheduled (ie 'Send Later')
            const isScheduled = this.type === 'schedule'

            //  If the message is scheduled, escape and go to special handling
            if (isScheduled) return this.sendScheduledInvoices()

            //  Start the loader
            this.loading = true

            //  Else, iterate through each invoice message
            this.invoiceMessages.forEach(async (message: Message) => {
                //  If the user changed the template using the template selector, we need to update all message's text
                //  All messages will have the same template, and therefore same message body ({{ InvoicePayLink }} tag will be processed server-side)
                message.text = this.message.text

                //  Make the type of the message 'invoice'
                message.type = 'invoice'

                //  Set the invoice_id field on the message
                message.invoice_id = message.invoice.id

                //  Post each message
                const messages = await this.$store.dispatch('messagesPost', message)

                //  Attach a sent_at date to the invoice so we can tell it was sent
                message.invoice.sent_at = messages.created_at

                //  Update the invoice
                this.$store.dispatch('invoicesPut', message.invoice)
            })

            //  Stop the loader
            this.loading = false

            //  Handle successfully sent messages
            this.onMessagesSent('invoices', this.invoiceMessages)
        },
        //  Post a standard sms to the API
        async sendStandardMessage(): Promise<void> {
            //  Start the loader
            this.loading = true

            //  Post the message to the API
            const messages = await this.$store.dispatch('messagesPost', this.message)

            // Stop the loader
            this.loading = false

            //  If the request failed, do nothing
            if (!messages) return

            //  Else, handle successfully sent messages
            this.onMessagesSent('standard', [messages])
        },
        //  Post scheduled invoice messages to the API
        async sendScheduledInvoices(): Promise<void> {
            //  If the user doesn't have access to scheduling messages, pop disabled dialog
            if (!this.schedulingEnabled) return this.popSchedulingDisabled()

            //  Start the loader
            this.loading = true

            //  Iterate through each invoiceMessage
            this.invoiceMessages.forEach(async (invoiceMessage: Message) => {
                //  If the user changed the template using the template selector, we need to update all message's text
                //  All messages will have the same template, and therefore same message body ({{ InvoicePayLink }} tag will be processed server-side)
                invoiceMessage.text = this.message.text

                //  Set the invoice_id field on the message
                invoiceMessage.invoice_id = invoiceMessage.invoice.id

                //  Set the schedule on the invoice
                invoiceMessage.schedules = this.message.schedules

                //  Post each schedule
                let scheduledMessages = await this.$store.dispatch(
                    'schedulesPost',
                    invoiceMessage
                )

                //  Once schedule is created and comes back, we need to attach its id to the invoice and save it
                invoiceMessage.invoice.schedule_id = scheduledMessages[0].schedule.id

                //  Save the schedule_id on the invoice
                this.$store.dispatch('invoicesPut', invoiceMessage.invoice)

                //  Update the client with the new schedule
                invoiceMessage.invoice.schedule = scheduledMessages[0].schedule
            })

            //  Stop the loader
            this.loading = false

            //  Handle successfully sent messages
            this.onMessagesSent('scheduledInvoices', this.invoiceMessages)
        },
        //  Post a scheduled message to the API
        async sendScheduledMessage(): Promise<void> {
            //  If the user doesn't have access to scheduling messages, pop disabled dialog
            if (!this.schedulingEnabled) return this.popSchedulingDisabled()

            //  Start the loader
            this.loading = true

            //  Else, send a single scheduled message request to the API
            const schedule = await this.$store.dispatch('schedulesPost', this.message)

            //  Stop the loader
            this.loading = false

            //  If the request failed, do nothing
            if (!schedule) return

            //  Else, handle successfully sent messages
            this.onMessagesSent('scheduled')
        },
        tagValues(person: Person): object {
            //  Create map for static message tags
            const tags = {
                '{{ OrganizationAddress }}': this.tenant.line1,
                '{{ OrganizationName }}': this.tenant.company_name,
                '{{ OrganizationTimezone }}': this.tenant.default_timezone,
                '{{ FirstName }}': person.name_given,
                '{{ LastName }}': person.name_family,
                '{{ Title }}': person.name_title,
                '{{ InvoicePayLink }}': 'https://learn.snapdesk.app/en/articles/5042842',
                '{{ InvoiceBalance }}': '$123.45',
                '{{ InvoiceDueDate }}': moment().format('Y-MM-DD'),
                '{{ InvoiceNumber }}': '####',
                '{{ User_FirstName }}': this.user.firstname,
                '{{ User_LastName }}': this.user.lastname,
                '{{ User_FullName }}': this.user.fullname
            }

            //  If the tenant has an SMS Account, add the msisdn (the SmsAccount may not be provisioned yet)
            if (this.smsAccount)
                tags['{{ OrganizationPhoneNumber }}'] = this.smsAccount.msisdn

            //  Check if we meet the basic requirements for parsing custom fields
            const meetsRequirements = person && this.extensionOptions

            //  Check if we need to process custom fields
            const hasExtensions = person.extensions ? true : false

            //  If the templated person contains custom fields, attach them as tags
            if (meetsRequirements && hasExtensions) {
                //  Iterate through extension options
                for (let i = 0; i < this.extensionOptions.length; i++) {
                    //  Get the option
                    const option = this.extensionOptions[i]

                    //  Get the templated person's extension
                    const value = person.extensions[option.name] || null

                    //  Attach the custom field tag, with person value
                    tags[`{{ ${option.name} }}`] = value
                }
            }

            //  Return the message tags map
            return tags
        },
        //  Create a new MessageTemplate
        async templatePost(): Promise<boolean> {
            //  Assemble the template payload
            const payload = {
                author_id: this.user.id,
                template_name: this.selectedTemplate,
                message_body: this.message.text,
                type: this.invoiceMessages ? 'invoice' : 'message'
            }

            //  Start loading spinner
            this.loading = true

            //  Send the request to the API
            await this.$store.dispatch('templatesPost', payload)

            //  Stop the loader
            this.loading = false

            //  Return true because success
            return true
        },
        //  Update an existing MessageTemplate
        async templatePut(): Promise<boolean> {
            //  Update the existing template's text
            this.selectedTemplate.message_body = this.message.text

            //  Start the loader
            this.loading = true

            //  Send the request to the API
            await this.$store.dispatch('templatesPut', this.selectedTemplate)

            //  Stop the loader
            this.loading = false

            //  Return true because success
            return true
        },
        //  Route the 'template save' request to the proper request type
        async templateSave(): Promise<boolean> {
            //  Get the input type from the saved Template
            const type = typeof this.selectedTemplate

            //  If the type is 'object', we know we're updating an existing template
            if (type === 'object') return await this.templatePut()

            //  If the typs is a 'string', we know we're creating a new template
            if (type === 'string') return await this.templatePost()
        },
        validateSchedules(): boolean {
            //  If schedules do NOT exist, they're not valid, return false
            if (!this.message.schedules) return false

            //  Create an errors container
            const errors = []

            //  Search for errors in the schedules array
            for (const s of this.message.schedules) {
                const v = s.next_send !== null && s.interval !== null
                if (!v) errors.push(s)
            }

            //  Check if errors were found
            const valid = errors.length === 0

            //  Update the validation state with the result
            this.schedulesValid = valid
        }
    }
})
